import type { PaginatedResult } from '@marketing-tool/shared';
import React from 'react';

import * as remote from '../data/remote-data';

/**
 * Generic hook that wraps a tRPC query call, manages RemoteData state,
 * and guards against stale request races (same pattern as makeReportHook).
 */
export function useTrpcQuery<T>(
    queryFn: (signal: AbortSignal) => Promise<T>,
    deps: React.DependencyList,
): { data: remote.RemoteData<T, string>; load: () => Promise<void> } {
    const [data, setData] = React.useState<remote.RemoteData<T, string>>(remote.NotAsked);
    const ref = React.useRef(0);
    const abortRef = React.useRef<AbortController>(null);

    const api = React.useMemo(
        () => ({
            async load() {
                const id = ++ref.current;
                const safeSet = (value: remote.RemoteData<T, string>) => {
                    if (id === ref.current) setData(value);
                };

                abortRef.current?.abort();
                const abortController = new AbortController();
                abortRef.current = abortController;

                safeSet(remote.Loading);

                try {
                    const result = await queryFn(abortController.signal);
                    safeSet(remote.Success(result));
                } catch (error) {
                    if (abortController.signal.aborted) return;
                    safeSet(remote.Failure(error instanceof Error ? error.message : String(error)));
                }
            },
        }),
        [ref, setData, ...deps],
    );

    React.useEffect(() => {
        void api.load();
        return () => abortRef.current?.abort();
    }, [api]);

    return React.useMemo(() => ({ data, ...api }), [data, api]);
}

/**
 * Hook that auto-loads all pages from a paginated tRPC query.
 * Each tRPC call returns one page; this hook accumulates all items.
 * Same race-condition protection as useTrpcQuery.
 */
export function useAllPages<T>(
    queryFn: (cursor: string | undefined, signal: AbortSignal) => Promise<PaginatedResult<T>>,
    deps: React.DependencyList,
): { data: remote.RemoteData<T[], string>; load: () => Promise<void> } {
    const [data, setData] = React.useState<remote.RemoteData<T[], string>>(remote.NotAsked);
    const ref = React.useRef(0);
    const abortRef = React.useRef<AbortController>(null);

    const api = React.useMemo(
        () => ({
            async load() {
                const id = ++ref.current;
                const safeSet = (value: remote.RemoteData<T[], string>) => {
                    if (id === ref.current) setData(value);
                };

                abortRef.current?.abort();
                const abortController = new AbortController();
                abortRef.current = abortController;

                safeSet(remote.Loading);

                try {
                    const allItems: T[] = [];
                    let cursor: string | undefined;

                    do {
                        const page = await queryFn(cursor, abortController.signal);
                        allItems.push(...page.items);
                        cursor = page.nextCursor ?? undefined;
                    } while (cursor);

                    safeSet(remote.Success(allItems));
                } catch (error) {
                    if (abortController.signal.aborted) return;
                    safeSet(remote.Failure(error instanceof Error ? error.message : String(error)));
                }
            },
        }),
        [ref, setData, ...deps],
    );

    React.useEffect(() => {
        void api.load();
        return () => abortRef.current?.abort();
    }, [api]);

    return React.useMemo(() => ({ data, ...api }), [data, api]);
}
